import React from 'react';

const features = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="14" cy="10" r="4" />
        <path d="M8 10a6 6 0 0 0 12 0" />
        <path d="M6 24c0-4.418 3.582-8 8-8s8 3.582 8 8" />
      </svg>
    ),
    title: 'Support SLA',
    description: 'Contractual uptime and performance guarantees, with reserved capacity sized to your volume.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 14l2.5 2.5L18 9" />
        <path d="M5 7l2-2h14l2 2v10l-2 2H7l-2-2V7z" />
        <path d="M9 22h10" />
        <path d="M14 19v3" />
      </svg>
    ),
    title: 'Dedicated Deployment Support',
    description: 'A dedicated engineer embedded in your team to get you live in a week.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="8" y="6" width="12" height="16" rx="2" />
        <circle cx="14" cy="15" r="2" />
        <path d="M14 13v-3" />
      </svg>
    ),
    title: 'SSO, OAuth & RBAC',
    description: 'Enterprise sign-on, OAuth2 for secure integrations, and granular access controls.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="5 17 10 12 14 16 19 9 23 13" />
        <line x1="5" y1="22" x2="23" y2="22" />
      </svg>
    ),
    title: 'Scalable Infrastructure',
    description: 'Scale up to millions of calls with sub-500ms latency.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="6" y="6" width="16" height="16" rx="2" strokeDasharray="3 3" />
        <path d="M11 14h6M14 11v6" />
      </svg>
    ),
    title: 'AI Guardrails',
    description: 'Built-in conversation guardrails prevent model hallucinations and ensure data integrity.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 4l8 3v7c0 5-4 8-8 9-4-1-8-4-8-9V7l8-3z" />
        <path d="M10 14l2.5 2.5L18 10" />
      </svg>
    ),
    title: 'Enterprise-Grade Security',
    description: 'End-to-end encryption, role-based access controls, and industry best practices to keep your data safe.',
  },
];

const metrics = [
  { value: '99.9%', label: 'Uptime SLA' },
  { value: '10M+', label: 'Calls handled' },
  { value: '<200ms', label: 'Response time' },
];

const EnterpriseSection = () => {
  return (
    <section
      style={{
        background: '#F8F9FC',
        paddingTop: '110px',
        paddingBottom: '110px',
      }}
    >
      <div
        style={{
          maxWidth: '1280px',
          margin: '0 auto',
          paddingLeft: '48px',
          paddingRight: '48px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: '90px',
          }}
          className="enterprise-layout"
        >
          {/* Left Column — 35% */}
          <div style={{ flex: '0 0 35%', maxWidth: '35%' }}>
            {/* Eyebrow */}
            <p
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.04em',
                color: '#4F6BFF',
                marginBottom: '20px',
                textTransform: 'uppercase',
              }}
            >
              Built for Enterprises
            </p>

            {/* Heading */}
            <h2
              style={{
                fontSize: '52px',
                fontWeight: '700',
                lineHeight: '1.1',
                color: '#111827',
                marginBottom: '20px',
                letterSpacing: '-0.02em',
              }}
            >
              Enterprise-ready
              <br />
              capabilities
            </h2>

            {/* Description */}
            <p
              style={{
                fontSize: '18px',
                color: '#6B7280',
                lineHeight: '1.65',
                maxWidth: '320px',
                marginBottom: '48px',
              }}
            >
              Everything a modern business needs to deploy AI voice agents at scale.
            </p>

            {/* Metrics Row */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '36px',
              }}
            >
              {metrics?.map(({ value, label }) => (
                <div key={label}>
                  <p
                    style={{
                      fontSize: '40px',
                      fontWeight: '700',
                      color: '#0F1D52',
                      lineHeight: '1',
                      marginBottom: '6px',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {value}
                  </p>
                  <p
                    style={{
                      fontSize: '13px',
                      color: '#9CA3AF',
                      fontWeight: '500',
                    }}
                  >
                    {label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column — 65% */}
          <div
            style={{
              flex: '0 0 calc(65% - 90px)',
              maxWidth: 'calc(65% - 90px)',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              columnGap: '48px',
              rowGap: '48px',
            }}
          >
            {features?.map(({ icon, title, description }) => (
              <div
                key={title}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: '20px',
                }}
              >
                {/* Icon */}
                <div
                  style={{
                    flexShrink: 0,
                    color: '#374151',
                    marginTop: '2px',
                  }}
                >
                  {icon}
                </div>

                {/* Text */}
                <div>
                  <h3
                    style={{
                      fontSize: '17px',
                      fontWeight: '600',
                      color: '#111827',
                      marginBottom: '8px',
                      lineHeight: '1.3',
                    }}
                  >
                    {title}
                  </h3>
                  <p
                    style={{
                      fontSize: '14px',
                      color: '#6B7280',
                      lineHeight: '1.65',
                    }}
                  >
                    {description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Responsive styles */}
      <style>{`
        @media (max-width: 1024px) {
          .enterprise-layout {
            flex-direction: column !important;
            gap: 56px !important;
          }
          .enterprise-layout > div {
            flex: 0 0 100% !important;
            max-width: 100% !important;
          }
        }
        @media (max-width: 640px) {
          .enterprise-layout > div:last-child {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

export default EnterpriseSection;
